<template>
  <div id="jci-trainers-preview" class="preview-container">
    <!-- Overlay Loading -->
    <v-overlay :value="ProgressLoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Premium Header -->
    <div class="preview-header">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content">
          <div class="d-flex align-center">
            <div class="header-icon-box">
              <v-icon large color="white">mdi-account-details</v-icon>
            </div>
            <div class="ml-4">
              <h1 class="header-title white--text mb-1">{{ pageTitle }}</h1>
              <p class="header-subtitle white--text mb-0">
                Trainer Profile Information
              </p>
            </div>
          </div>
        </div>
        <v-btn icon dark @click="closePrompt" class="close-btn">
          <v-icon>mdi-close-circle-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="LoadingFlag" class="text-center py-12">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
      <p class="mt-4 text-h6 primary--text">Loading trainer details...</p>
    </div>

    <!-- Content -->
    <div v-if="!LoadingFlag" class="preview-content-wrapper pa-6">
      <v-form
        ref="form1"
        v-model="valid1"
        lazy-validation
        v-on:submit.prevent="submitForm"
      >
        <!-- Personal Information Section -->
        <v-card v-if="RecordFlag" elevation="8" class="info-card mb-6">
          <v-card-title class="section-title pb-4">
            <v-icon small color="primary" class="mr-2"
              >mdi-account-circle</v-icon
            >
            Personal Information
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <v-row wrap>
              <v-col cols="12" sm="6" lg="3" md="3" class="mb-4">
                <div class="info-item">
                  <div class="info-icon">
                    <v-icon small color="primary">mdi-identifier</v-icon>
                  </div>
                  <div class="info-content">
                    <p class="info-label">Membership ID</p>
                    <p class="info-value">{{ row.MembershipId }}</p>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" sm="6" lg="3" md="3" class="mb-4">
                <div class="info-item">
                  <div class="info-icon">
                    <v-icon small color="primary">mdi-account</v-icon>
                  </div>
                  <div class="info-content">
                    <p class="info-label">Full Name</p>
                    <p class="info-value">{{ row.FullName }}</p>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" sm="6" lg="3" md="3" class="mb-4">
                <div class="info-item">
                  <div class="info-icon">
                    <v-icon small color="primary">mdi-school</v-icon>
                  </div>
                  <div class="info-content">
                    <p class="info-label">Trainer Type</p>
                    <p class="info-value">{{ row.TrainerTypeName }}</p>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" sm="6" lg="3" md="3" class="mb-4">
                <div class="info-item">
                  <div class="info-icon">
                    <v-icon small color="primary">mdi-calendar-check</v-icon>
                  </div>
                  <div class="info-content">
                    <p class="info-label">Trained Date</p>
                    <p class="info-value">{{ row.TrainerDate }}</p>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Timeline Section -->
        <v-card elevation="8" class="info-card mb-6" v-if="RecordFlag">
          <v-card-title class="section-title pb-4">
            <v-icon small color="primary" class="mr-2">mdi-timeline</v-icon>
            Timeline
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <v-row wrap>
              <v-col cols="12" sm="6" lg="6" md="6" class="mb-4">
                <div class="info-item">
                  <div class="info-icon">
                    <v-icon small color="success">mdi-plus-circle</v-icon>
                  </div>
                  <div class="info-content">
                    <p class="info-label">Created Date</p>
                    <p class="info-value">{{ row.CreatedTxt }}</p>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" sm="6" lg="6" md="6" class="mb-4">
                <div class="info-item">
                  <div class="info-icon">
                    <v-icon small color="warning">mdi-update</v-icon>
                  </div>
                  <div class="info-content">
                    <p class="info-label">Responded Date</p>
                    <p class="info-value">{{ row.UpdatedTxt }}</p>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Attachments Section -->
        <v-card
          elevation="8"
          class="info-card mb-6"
          v-if="RecordFlag && row.AllImages && row.AllImages.length > 0"
        >
          <v-card-title class="section-title pb-4">
            <v-icon small color="primary" class="mr-2">mdi-attachment</v-icon>
            Attachments
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <div class="attachments-grid">
              <viewer rebuild :images="row.AllImages">
                <div
                  class="attachment-item"
                  v-for="src in row.AllImages"
                  :key="src"
                >
                  <img class="attachment-image" :src="src" :alt="src" />
                </div>
              </viewer>
            </div>
          </v-card-text>
        </v-card>

        <!-- Admin Action Section -->
        <v-card
          elevation="8"
          class="action-card"
          v-if="RecordFlag && (row.ActiveStatus == 2 || row.ActiveStatus == 3)"
        >
          <v-card-title class="section-title pb-4 warning--text">
            <v-icon small color="warning" class="mr-2">mdi-shield-admin</v-icon>
            Admin Approval
            <v-chip small label color="warning" text-color="white" class="ml-2">
              Admin Only
            </v-chip>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <v-row wrap>
              <v-col cols="12">
                <label class="form-label">
                  <v-icon small class="mr-2" color="primary"
                    >mdi-comment-multiple</v-icon
                  >
                  Description (Optional)
                </label>
                <v-textarea
                  v-model="Description"
                  placeholder="Enter approval description..."
                  outlined
                  rounded
                  dense
                  rows="4"
                  class="description-field"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Action Buttons -->
            <v-row wrap class="mt-4">
              <v-col cols="12" sm="6" md="6" lg="4" class="d-flex">
                <v-btn
                  @click.prevent="confirmAlert(1)"
                  :loading="ApproveLoadingFlag"
                  color="success"
                  elevation="6"
                  rounded
                  large
                  block
                  class="action-btn approve-btn font-weight-bold white--text"
                >
                  <v-icon left>mdi-check-circle</v-icon>
                  Approve
                </v-btn>
              </v-col>

              <v-col cols="12" sm="6" md="6" lg="4" class="d-flex">
                <v-btn
                  @click.prevent="confirmAlert(0)"
                  :loading="ApproveRejectLoadingFlag"
                  color="warning"
                  elevation="6"
                  rounded
                  large
                  block
                  class="action-btn reject-btn font-weight-bold white--text"
                >
                  <v-icon left>mdi-close-circle</v-icon>
                  Reject
                </v-btn>
              </v-col>

              <v-col cols="12" sm="6" md="6" lg="4" class="d-flex">
                <v-btn
                  @click="closePrompt"
                  color="primary"
                  elevation="6"
                  rounded
                  large
                  block
                  class="action-btn cancel-btn font-weight-bold white--text"
                >
                  <v-icon left>mdi-close</v-icon>
                  Cancel
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-form>
    </div>
  </div>
</template>
    
    <script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  props: {
    previewRecordFlag: {
      type: Boolean,
      required: true,
    },
    recordData: {
      required: true,
    },
    pageTitle: {
      type: String,
    },
  },

  data() {
    return {
      Width: 500,

      valid1: false,
      valid2: false,
      ProgressLoadingFlag: false,
      ResultFlag: false,
      RecordFlag: false,
      LoadingFlag: false,
      row: {},
      PaymentDetails: {},
      selected: [],

      Description: "",

      ApprovedDate: new Date().toISOString().slice(0, 10),
      menu1: false,

      ApproveLoadingFlag: false,
      ApproveRejectLoadingFlag: false,
      ConfirmationLoadingFlag: false,
      ConfirmationRejectLoadingFlag: false,

      dialog: false,
      dialog1: false,

      tableColumns: [],
      tableOptions: [],
      tableData: [],
    };
  },
  computed: {},
  watch: {},
  created() {
    // this.initialize()
  },
  methods: {
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hidePreviewRecordPrompt", this.ResultFlag);
    },
    refreshPageData() {
      console.log("refreshPageData called");
      var recordData = this.recordData;
      console.log("recordData=" + JSON.stringify(recordData));
      this.getTableRecords();
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var TrainerId = this.recordData.TrainerId;
      var UserInterface = 1;
      console.log(", TrainerId=" + TrainerId);

      if (UserInterface != "") {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/trainer-details/show";
        var upload = {
          UserInterface: UserInterface,
          TrainerId: TrainerId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        this.row = {};
        this.RecordFlag = false;

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.LoadingFlag = true;
        this.Documentpath = "";

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            thisIns.RecordFlag = flag;

            if (flag == 1) {
              thisIns.row = records;
              console.log("records=" + JSON.stringify(records));
              // thisIns.toast("success", output);
            } else {
              // thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.toast("error", error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Your session is expired. Kindly login again. ";
        console.log("error=" + message);
      }
    },
    confirmAlert(status) {
      console.log("confirmAlert is called");
      console.log({ status });
      var htmlTxt = "";

      //   var validate = this.$refs.form1.validate();
      var validate = true;
      console.log({ validate });

      if (validate) {
        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Continue`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            switch (status) {
              case 0:
                this.approvalSubmitForm(0);

                break;

              case 1:
                this.approvalSubmitForm(1);

                break;

              default:
                break;
            }
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      }
    },
    approvalSubmitForm(status) {
      console.log("approvalSubmitForm is called");

      console.log({ status });

      var tr = this.row;
      console.log({ tr });

      this.ProgressLoadingFlag = true;
      this.ResultFlag = false;

      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      var TrainerType = tr.TrainerTypeId;
      var TrainerId = tr.TrainerId;
      var MemberId = tr.MemberId;
      var Description = this.Description;

      console.log({ TrainerType });
      console.log({ TrainerId });
      console.log({ MemberId });
      console.log({ Description });

      var server_url = companyConfig.apiURL;
      var add_url = server_url + "api/trainer-details/approval";
      console.log({ add_url });

      var upload = {
        UserInterface: 1,
        TrainerId: TrainerId,
        TrainerType: TrainerType,
        MemberId: MemberId,
        Description: Description,
        Status: status,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = {};
      var table = {};
      var successTxt = "";
      var errorTxt = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.ProgressLoadingFlag = false;
          output = response.data.output;
          flag = response.data.flag;
          output = response.data.output;
          records = response.data.records;
          console.log("output=" + output + ", flag=" + flag);
          successTxt = response.data.success;
          errorTxt = response.data.error;
          console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

          if (flag == 1) {
            thisIns.sweetAlert("success", output, true);
            thisIns.ResultFlag = true;
            thisIns.closePrompt();
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.SearchFlag = false;
          thisIns.ProgressLoadingFlag = true;
        });
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
    <style lang="scss" scoped>
// Color Palette
$primary-color: #667eea;
$secondary-color: #764ba2;
$gradient-main: linear-gradient(
  135deg,
  $primary-color 0%,
  $secondary-color 100%
);
$success-color: #10b981;
$warning-color: #f59e0b;
$danger-color: #f44336;
$light-bg: #f5f7fa;

// Animation Keyframes
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

#jci-trainers-preview {
  .preview-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  // HEADER
  // ============================================
  .preview-header {
    flex-shrink: 0;
    background: $gradient-main;
    border-radius: 16px 16px 0 0;
    overflow: visible;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    box-shadow: 0 20px 60px rgba(102, 126, 234, 0.4);
  }

  .header-gradient {
    position: relative;
    overflow: visible;
  }

  .header-backdrop-effect {
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    animation: rotate 20s linear infinite;
  }

  .header-content {
    position: relative;
    z-index: 1;
    padding: 24px 32px;
    display: flex;
    align-items: center;
  }

  .header-icon-box {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    border-radius: 14px;
    padding: 10px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    animation: float 3s ease-in-out infinite;
    flex-shrink: 0;
  }

  .header-title {
    font-size: 24px;
    font-weight: 700;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
    animation: fadeInUp 0.8s ease-out;
    letter-spacing: 0.5px;
  }

  .header-subtitle {
    font-size: 14px;
    opacity: 0.95;
    animation: fadeInUp 1s ease-out;
    letter-spacing: 0.3px;
  }

  .ml-4 {
    margin-left: 16px;
  }

  .close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
    transition: all 0.3s ease;
    background: rgba(255, 255, 255, 0.15) !important;

    &:hover {
      background: rgba(255, 255, 255, 0.25) !important;
      transform: scale(1.1);
    }

    .v-icon {
      font-size: 28px !important;
    }
  }

  // ============================================
  // CONTENT WRAPPER
  // ============================================
  .preview-content-wrapper {
    flex: 1;
    overflow-y: auto;
    background: $light-bg;
  }

  // ============================================
  // INFO CARDS
  // ============================================
  .info-card {
    border-radius: 14px !important;
    border: none;
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2);
    animation: fadeInUp 0.8s ease-out backwards;
    overflow: hidden;

    .section-title {
      font-size: 16px;
      font-weight: 700;
      color: #333;
      display: flex;
      align-items: center;
      padding: 16px 24px;
      background: rgba(102, 126, 234, 0.05);
    }
  }

  .info-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;

    .info-icon {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(102, 126, 234, 0.1);
      border-radius: 8px;
    }

    .info-content {
      flex: 1;

      .info-label {
        font-size: 12px;
        font-weight: 600;
        color: #999;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        margin: 0 0 4px 0;
      }

      .info-value {
        font-size: 15px;
        font-weight: 600;
        color: #333;
        margin: 0;
        word-break: break-word;
      }
    }
  }

  // ============================================
  // ATTACHMENTS SECTION
  // ============================================
  .attachments-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 16px;

    .attachment-item {
      position: relative;
      border-radius: 12px;
      overflow: hidden;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
      transition: all 0.3s ease;

      .attachment-image {
        width: 100%;
        height: 120px;
        object-fit: cover;
        display: block;
        border: 3px solid $primary-color;
        border-radius: 12px;
      }

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
      }
    }
  }

  // ============================================
  // ACTION CARD (ADMIN)
  // ============================================
  .action-card {
    border-radius: 14px !important;
    border: 2px solid $warning-color;
    background: rgba(245, 158, 11, 0.05);
    box-shadow: 0 12px 40px rgba(245, 158, 11, 0.15);
    animation: fadeInUp 0.8s ease-out backwards;
  }

  .form-label {
    display: flex;
    align-items: center;
    font-weight: 600;
    color: #333;
    font-size: 14px;
    margin-bottom: 8px;
  }

  .description-field {
    ::v-deep {
      .v-field__input {
        font-size: 14px;
      }
    }
  }

  // ============================================
  // ACTION BUTTONS
  // ============================================
  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25) !important;
    }

    .v-icon {
      font-size: 18px !important;
    }
  }

  .approve-btn {
    background: linear-gradient(
      135deg,
      $success-color 0%,
      #059669 100%
    ) !important;
  }

  .reject-btn {
    background: linear-gradient(
      135deg,
      $warning-color 0%,
      #d97706 100%
    ) !important;
  }

  .cancel-btn {
    background: linear-gradient(
      135deg,
      $primary-color 0%,
      $secondary-color 100%
    ) !important;
  }

  // ============================================
  // UTILITY CLASSES
  // ============================================
  .py-12 {
    padding-top: 48px;
    padding-bottom: 48px;
  }

  .pa-6 {
    padding: 24px;
  }

  .mt-4 {
    margin-top: 16px;
  }

  .mb-6 {
    margin-bottom: 24px;
  }

  .mb-4 {
    margin-bottom: 16px;
  }

  .mb-1 {
    margin-bottom: 4px;
  }

  .ml-2 {
    margin-left: 8px;
  }

  .mr-2 {
    margin-right: 8px;
  }

  .d-flex {
    display: flex;
  }

  .align-center {
    align-items: center;
  }

  .justify-space-between {
    justify-content: space-between;
  }

  .white--text {
    color: white;
  }

  .primary--text {
    color: $primary-color;
  }

  .warning--text {
    color: $warning-color;
  }

  .font-weight-bold {
    font-weight: 700;
  }

  .text-h6 {
    font-size: 20px;
  }

  .text-center {
    text-align: center;
  }

  .text-right {
    text-align: right;
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 960px) {
    .header-title {
      font-size: 20px;
    }

    .header-subtitle {
      font-size: 12px;
    }

    .info-item {
      margin-bottom: 12px;
    }
  }

  @media (max-width: 600px) {
    .header-content {
      padding: 16px 20px;
    }

    .header-title {
      font-size: 16px;
    }

    .header-subtitle {
      font-size: 11px;
    }

    .preview-content-wrapper {
      padding: 16px !important;
    }

    .close-btn .v-icon {
      font-size: 24px !important;
    }

    .attachments-grid {
      grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
      gap: 12px;

      .attachment-item .attachment-image {
        height: 100px;
      }
    }

    .action-btn {
      font-size: 12px !important;
      padding: 10px 12px !important;
    }
  }
}
</style>